// RD-163: модель раздела «Настройки» — каталоги существующих полей, черновики и diff.
// Без Vue и API: функции чистые, черновик хранит только правки пользователя поверх baseline,
// поэтому новое прочитанное с сервера значение не затирается старой копией.

import type { EmployeeStatisticsSettings, Role } from '@/types'

export type SettingsSectionId = 'general' | 'roles' | 'positions' | 'exchange'

/* ---------- GlobalSettings (GET/PATCH /api/settings/global/) ---------- */

export const GLOBAL_NUMBER_KEYS = [
    'VIOLATION_TOLERANCE_MINUTES',
    'VIOLATION_TOLERANCE_MINUTES_EXIT',
    'MAX_SECONDS_BEFORE_SHIFT_WHEN_EMPLOYEE_CAN_MAKE_CHECKIN',
    'MINUTES_UNTIL_AVAILABLE_INNER_WORKERS',
    'MINUTES_UNTIL_AVAILABLE_OUTER_WORKERS',
] as const

export const GLOBAL_BOOLEAN_KEYS = [
    'DISTRIBUTION_SETTINGS_GEO',
    'DISTRIBUTION_SETTINGS_SPECIALIZATION',
    'DISTRIBUTION_SETTINGS_PRIORITY',
    'FAST_RULE',
    'AUTO_APPROVE',
] as const

export type GlobalNumberKey = typeof GLOBAL_NUMBER_KEYS[number]
export type GlobalBooleanKey = typeof GLOBAL_BOOLEAN_KEYS[number]
export type GlobalKey = GlobalNumberKey | GlobalBooleanKey
export type GlobalSettings = Record<GlobalNumberKey, number> & Record<GlobalBooleanKey, boolean>
export type GlobalSettingsPatch = Partial<GlobalSettings>

/** Черновик: числа — введённый текст (пустое/невалидное не превращается в 0), флаги — boolean. */
export type GlobalDraft = Partial<Record<GlobalNumberKey, string> & Record<GlobalBooleanKey, boolean>>

export const TIME_FIELDS: ReadonlyArray<{ key: GlobalNumberKey, label: string, unit: 'мин' | 'сек' }> = [
    { key: 'VIOLATION_TOLERANCE_MINUTES', label: 'Допустимое опоздание', unit: 'мин' },
    { key: 'VIOLATION_TOLERANCE_MINUTES_EXIT', label: 'Допустимый ранний уход', unit: 'мин' },
    { key: 'MAX_SECONDS_BEFORE_SHIFT_WHEN_EMPLOYEE_CAN_MAKE_CHECKIN', label: 'Отметка до начала смены', unit: 'сек' },
]

export const EXCHANGE_DELAY_FIELDS: ReadonlyArray<{ key: GlobalNumberKey, label: string, unit: 'мин' }> = [
    { key: 'MINUTES_UNTIL_AVAILABLE_INNER_WORKERS', label: 'Внутренняя сеть', unit: 'мин' },
    { key: 'MINUTES_UNTIL_AVAILABLE_OUTER_WORKERS', label: 'Аутсорс', unit: 'мин' },
]

// Подписи — прежние; «Как работает» — описания полей схемы main.api_schema.GlobalSettings.
export const EXCHANGE_RULE_FIELDS: ReadonlyArray<{ key: GlobalBooleanKey, label: string, description: string }> = [
    { key: 'DISTRIBUTION_SETTINGS_GEO', label: 'Учитывать регион', description: 'Учитывать при распределении регион' },
    { key: 'DISTRIBUTION_SETTINGS_SPECIALIZATION', label: 'Учитывать специализацию', description: 'Учитывать при распределении специализацию' },
    { key: 'DISTRIBUTION_SETTINGS_PRIORITY', label: 'Учитывать приоритеты', description: 'Учитывать при распределении приоритеты' },
    { key: 'FAST_RULE', label: 'Правило срочности', description: 'Правило срочности' },
    { key: 'AUTO_APPROVE', label: 'Автоматическое подтверждение', description: 'Автоматическое подтверждение отклика сотрудника на вакансию' },
]

export const GENERAL_GLOBAL_KEYS: ReadonlyArray<GlobalKey> = TIME_FIELDS.map((field) => field.key)
export const EXCHANGE_GLOBAL_KEYS: ReadonlyArray<GlobalKey> = [
    ...EXCHANGE_DELAY_FIELDS.map((field) => field.key),
    ...EXCHANGE_RULE_FIELDS.map((field) => field.key),
]

/* ---------- EmployeeStatisticsSettings ---------- */

export type StatisticsKey = keyof EmployeeStatisticsSettings
export type StatisticsDraft = Partial<EmployeeStatisticsSettings>

// Порядок — прежний порядок полей настроек (две колонки по пять).
export const STATISTIC_FIELDS: ReadonlyArray<{ key: StatisticsKey, code: string, label: string }> = [
    { key: 'show_working_days', code: 'РД', label: 'Рабочие дни' },
    { key: 'show_plan_hours', code: 'ПЧ', label: 'План часов' },
    { key: 'show_fact_hours', code: 'ФЧ', label: 'Факт часов' },
    { key: 'show_additional_plan', code: 'ПД', label: 'План доп' },
    { key: 'show_additional_fact', code: 'ФД', label: 'Факт доп' },
    { key: 'show_exchange_hours', code: 'БСЧ', label: 'Часы биржи смен' },
    { key: 'show_hours_norm', code: 'НЧ', label: 'Норма часов' },
    { key: 'show_overtime_from_norm', code: 'ПН', label: 'Переработка от нормы' },
    { key: 'show_all_department_plan_hours', code: 'ПВ', label: 'План всего' },
    { key: 'show_all_department_fact_hours', code: 'ФВ', label: 'Факт всего' },
]

/* ---------- Числа ---------- */

export const INT_MAX = 2147483647

/** Целое >= 0 из текста поля; null — пусто или невалидно (не 0). */
export function parseNonNegativeInt(text: string | number | null | undefined, max = INT_MAX): number | null {
    const value = String(text ?? '').trim()
    if (!/^\d+$/.test(value)) return null
    const parsed = Number(value)
    return Number.isSafeInteger(parsed) && parsed <= max ? parsed : null
}

export function formatSecondsAsMinutes(seconds: number | null): string {
    if (seconds === null) return '—'
    const minutes = Math.floor(seconds / 60)
    const rest = seconds % 60
    if (!rest) return `${minutes} мин`
    return minutes ? `${minutes} мин ${rest} сек` : `${rest} сек`
}

/* ---------- Изменения для панели ---------- */

export interface SettingsChange {
    /** Стабильный id правки внутри области: по нему выполняется отмена. */
    id: string
    block: string
    label: string
    techKey?: string
    before: string
    after: string
    invalid?: boolean
    /** Если задано — отмена этой правки недоступна, текст объясняет почему. */
    revertBlockedReason?: string
    /** id элемента на странице для подсветки/перехода. */
    anchor?: string
}

export const boolText = (value: boolean | null | undefined) => (value ? 'вкл' : 'выкл')
const valueText = (value: number | null | undefined, unit = '') =>
    value === null || value === undefined ? 'пусто' : `${value}${unit ? ` ${unit}` : ''}`

/* ---------- Global diff ---------- */

export function isGlobalDraftChanged(baseline: GlobalSettings, draft: GlobalDraft, key: GlobalKey): boolean {
    if (!(key in draft)) return false
    const value = draft[key]
    if (typeof value === 'boolean') return value !== baseline[key]
    return parseNonNegativeInt(value) !== baseline[key]
}

export function isGlobalDraftInvalid(draft: GlobalDraft, key: GlobalKey): boolean {
    const value = draft[key]
    return typeof value === 'string' && parseNonNegativeInt(value) === null
}

export function globalPatch(baseline: GlobalSettings, draft: GlobalDraft, keys: ReadonlyArray<GlobalKey>): GlobalSettingsPatch {
    const patch: Record<string, number | boolean> = {}
    keys.forEach((key) => {
        if (!isGlobalDraftChanged(baseline, draft, key) || isGlobalDraftInvalid(draft, key)) return
        const value = draft[key]
        patch[key] = typeof value === 'boolean' ? value : parseNonNegativeInt(value) as number
    })
    return patch as GlobalSettingsPatch
}

/** Убрать из черновика правки, совпавшие с новым baseline (после ответа или GET). */
export function pruneGlobalDraft(baseline: GlobalSettings, draft: GlobalDraft): GlobalDraft {
    const next: GlobalDraft = { ...draft }
    ;(Object.keys(next) as GlobalKey[]).forEach((key) => {
        if (!isGlobalDraftChanged(baseline, next, key) && !isGlobalDraftInvalid(next, key)) delete next[key]
    })
    return next
}

/* ---------- Statistics diff ---------- */

export function statisticsPatch(baseline: EmployeeStatisticsSettings, draft: StatisticsDraft): StatisticsDraft {
    const patch: StatisticsDraft = {}
    STATISTIC_FIELDS.forEach(({ key }) => {
        if (key in draft && draft[key] !== baseline[key]) patch[key] = draft[key]
    })
    return patch
}

export function pruneStatisticsDraft(baseline: EmployeeStatisticsSettings, draft: StatisticsDraft): StatisticsDraft {
    return statisticsPatch(baseline, draft)
}

/* ---------- Обеденные правила ---------- */

export interface LunchRule {
    id: string
    shift_duration_seconds: number
    break_duration_seconds: number
}

export const LUNCH_RULES_LIMIT = 5
// Значения в UI — минуты, в API — секунды (integer 0..INT_MAX).
export const LUNCH_MINUTES_MAX = Math.floor(INT_MAX / 60)

export interface LunchDraftRow {
    /** Локальный ключ строки; для существующих правил равен id. */
    key: string
    /** UUID сервера; null — новое правило до ответа POST. */
    id: string | null
    to: string
    brk: string
}

export interface LunchDraft {
    rows: LunchDraftRow[]
    /** Существующие правила, удалённые в черновике. */
    deleted: string[]
}

export const lunchToMinutes = (seconds: number) => seconds / 60

const minutesText = (seconds: number) => String(lunchToMinutes(seconds))

export function sortLunchRules(rules: LunchRule[]): LunchRule[] {
    return [...rules].sort((a, b) => a.shift_duration_seconds - b.shift_duration_seconds)
}

export function lunchDraftFromRules(rules: LunchRule[]): LunchDraft {
    return {
        rows: sortLunchRules(rules).map((rule) => ({
            key: rule.id,
            id: rule.id,
            to: minutesText(rule.shift_duration_seconds),
            brk: minutesText(rule.break_duration_seconds),
        })),
        deleted: [],
    }
}

export const parseLunchMinutes = (text: string) => parseNonNegativeInt(text, LUNCH_MINUTES_MAX)

/** Ошибки строк: границы строго возрастают от 0, перерыв — целое >= 0. */
export function lunchRowErrors(rows: LunchDraftRow[]): Record<string, { to?: string, brk?: string }> {
    const errors: Record<string, { to?: string, brk?: string }> = {}
    let previous = 0
    rows.forEach((row) => {
        const to = parseLunchMinutes(row.to)
        const brk = parseLunchMinutes(row.brk)
        const rowErrors: { to?: string, brk?: string } = {}
        if (to === null) rowErrors.to = 'Введите целое число минут'
        else if (to <= previous) rowErrors.to = `Должно быть больше ${previous}`
        if (brk === null) rowErrors.brk = 'Введите целое число минут'
        if (rowErrors.to || rowErrors.brk) errors[row.key] = rowErrors
        if (to !== null && to > previous) previous = to
    })
    return errors
}

export type LunchOperation =
    | { type: 'delete', id: string, rule: LunchRule }
    | { type: 'patch', id: string, rowKey: string, data: Partial<Omit<LunchRule, 'id'>> }
    | { type: 'create', rowKey: string, data: Omit<LunchRule, 'id'> }

/** Очередь записи обедов: DELETE → PATCH → POST, внутри — порядок диапазонов. */
export function lunchOperations(baseline: LunchRule[], draft: LunchDraft): LunchOperation[] {
    const byId = new Map(baseline.map((rule) => [rule.id, rule]))
    const deletes: LunchOperation[] = sortLunchRules(baseline)
        .filter((rule) => draft.deleted.includes(rule.id))
        .map((rule) => ({ type: 'delete', id: rule.id, rule }))
    const patches: LunchOperation[] = []
    const creates: LunchOperation[] = []

    draft.rows.forEach((row) => {
        const to = parseLunchMinutes(row.to)
        const brk = parseLunchMinutes(row.brk)
        if (row.id === null) {
            if (to !== null && brk !== null) {
                creates.push({ type: 'create', rowKey: row.key, data: { shift_duration_seconds: to * 60, break_duration_seconds: brk * 60 } })
            }
            return
        }
        const rule = byId.get(row.id)
        if (!rule) return
        const data: Partial<Omit<LunchRule, 'id'>> = {}
        if (to !== null && to * 60 !== rule.shift_duration_seconds) data.shift_duration_seconds = to * 60
        if (brk !== null && brk * 60 !== rule.break_duration_seconds) data.break_duration_seconds = brk * 60
        if (Object.keys(data).length) patches.push({ type: 'patch', id: row.id, rowKey: row.key, data })
    })

    return [...deletes, ...patches, ...creates]
}

export function lunchChanges(baseline: LunchRule[], draft: LunchDraft): SettingsChange[] {
    const block = 'Обеденные перерывы'
    const byId = new Map(baseline.map((rule) => [rule.id, rule]))
    const changes: SettingsChange[] = []
    const errors = lunchRowErrors(draft.rows)
    let previous = 0

    draft.rows.forEach((row, index) => {
        const to = parseLunchMinutes(row.to)
        const from = previous
        if (to !== null && to > previous) previous = to
        const range = `${from}–${to ?? '?'} мин`
        const anchor = `lunch-row-${row.key}`
        if (row.id === null) {
            changes.push({
                id: `lunch:create:${row.key}`,
                block,
                label: `Новое правило №${index + 1}`,
                before: 'нет',
                after: `${range} · перерыв ${parseLunchMinutes(row.brk) ?? '?'} мин`,
                invalid: Boolean(errors[row.key]),
                anchor,
            })
            return
        }
        const rule = byId.get(row.id)
        if (!rule) return
        if (to !== lunchToMinutes(rule.shift_duration_seconds) || errors[row.key]?.to) {
            changes.push({
                id: `lunch:to:${row.key}`,
                block,
                label: `Правило №${index + 1}: до`,
                before: `${lunchToMinutes(rule.shift_duration_seconds)} мин`,
                after: to === null ? 'не заполнено' : `${to} мин`,
                invalid: Boolean(errors[row.key]?.to),
                anchor,
            })
        }
        const brk = parseLunchMinutes(row.brk)
        if (brk !== lunchToMinutes(rule.break_duration_seconds) || errors[row.key]?.brk) {
            changes.push({
                id: `lunch:brk:${row.key}`,
                block,
                label: `Правило №${index + 1}: перерыв`,
                before: `${lunchToMinutes(rule.break_duration_seconds)} мин`,
                after: brk === null ? 'не заполнено' : `${brk} мин`,
                invalid: Boolean(errors[row.key]?.brk),
                anchor,
            })
        }
    })

    sortLunchRules(baseline)
        .filter((rule) => draft.deleted.includes(rule.id))
        .forEach((rule) => {
            changes.push({
                id: `lunch:delete:${rule.id}`,
                block,
                label: 'Удалено правило',
                before: `до ${lunchToMinutes(rule.shift_duration_seconds)} мин · ${lunchToMinutes(rule.break_duration_seconds)} мин`,
                after: 'удалено',
            })
        })

    return changes
}

/** Отмена одной правки обеда. */
export function revertLunchChange(baseline: LunchRule[], draft: LunchDraft, changeId: string): LunchDraft {
    const [, kind, key] = changeId.split(':')
    const rows = draft.rows.map((row) => ({ ...row }))
    if (kind === 'create') {
        return { rows: rows.filter((row) => row.key !== key), deleted: [...draft.deleted] }
    }
    if (kind === 'delete') {
        const rule = baseline.find((item) => item.id === key)
        if (!rule) return draft
        rows.push({ key: rule.id, id: rule.id, to: minutesText(rule.shift_duration_seconds), brk: minutesText(rule.break_duration_seconds) })
        return { rows: sortLunchDraftRows(rows), deleted: draft.deleted.filter((id) => id !== key) }
    }
    const row = rows.find((item) => item.key === key)
    const rule = row?.id ? baseline.find((item) => item.id === row.id) : undefined
    if (!row || !rule) return draft
    if (kind === 'to') row.to = minutesText(rule.shift_duration_seconds)
    if (kind === 'brk') row.brk = minutesText(rule.break_duration_seconds)
    return { rows, deleted: [...draft.deleted] }
}

/** Порядок строк по границе; невалидные остаются на своём месте относительно соседей. */
export function sortLunchDraftRows(rows: LunchDraftRow[]): LunchDraftRow[] {
    return rows
        .map((row, index) => ({ row, index, to: parseLunchMinutes(row.to) }))
        .sort((a, b) => {
            if (a.to === null || b.to === null) return a.index - b.index
            return a.to - b.to || a.index - b.index
        })
        .map((item) => item.row)
}

/**
 * Сверка черновика с перечитанным списком: правки сохраняются по ID, подтверждённые
 * удаления уходят, появившиеся на сервере правила добавляются как неизменённые.
 * newServerIds — правила, которых не было в прежнем baseline (возможен результат неоднозначного POST).
 */
export function reconcileLunchDraft(previousBaseline: LunchRule[], fresh: LunchRule[], draft: LunchDraft): { draft: LunchDraft, newServerIds: string[] } {
    const freshById = new Map(fresh.map((rule) => [rule.id, rule]))
    const previousById = new Map(previousBaseline.map((rule) => [rule.id, rule]))
    const freshIds = new Set(freshById.keys())
    const knownIds = new Set(previousById.keys())
    // Для существующих правил переносим только поля, изменённые пользователем относительно
    // прежнего baseline; неизменённые берём из свежего списка (не перезаписываем чужие правки).
    const rebaseField = (text: string, previousSeconds: number | undefined, freshSeconds: number) => {
        const unchanged = previousSeconds !== undefined && parseLunchMinutes(text) === lunchToMinutes(previousSeconds)
        return unchanged ? minutesText(freshSeconds) : text
    }
    const rows = draft.rows
        .filter((row) => row.id === null || freshIds.has(row.id))
        .map((row) => {
            if (row.id === null) return { ...row }
            const freshRule = freshById.get(row.id) as LunchRule
            const previousRule = previousById.get(row.id)
            return {
                ...row,
                to: rebaseField(row.to, previousRule?.shift_duration_seconds, freshRule.shift_duration_seconds),
                brk: rebaseField(row.brk, previousRule?.break_duration_seconds, freshRule.break_duration_seconds),
            }
        })
    const deleted = draft.deleted.filter((id) => freshIds.has(id))
    const present = new Set([...rows.map((row) => row.id), ...deleted])
    const newServerIds: string[] = []

    sortLunchRules(fresh).forEach((rule) => {
        if (present.has(rule.id)) return
        if (!knownIds.has(rule.id)) newServerIds.push(rule.id)
        rows.push({ key: rule.id, id: rule.id, to: minutesText(rule.shift_duration_seconds), brk: minutesText(rule.break_duration_seconds) })
    })

    return { draft: { rows: sortLunchDraftRows(rows), deleted }, newServerIds }
}

/* ---------- Роли и права ---------- */

export type RoleBooleanKey = {
    [K in keyof Role]-?: Role[K] extends boolean | undefined ? K : never
}[keyof Role]

export const ROLE_DAYS_KEY = 'days_in_past_to_manage_fact_time' as const

export interface RolePermission {
    key: RoleBooleanKey
    label: string
}

export interface RoleGroup {
    id: string
    label: string
    permissions: RolePermission[]
}

// Прежние группы, подписи и порядок формы ролей (до RD-163 — RoleItem.vue).
export const ROLE_GROUPS: ReadonlyArray<RoleGroup> = [
    { id: 'general', label: 'Общий доступ', permissions: [
        { key: 'can_access_main', label: 'Доступ в основную систему' },
        { key: 'can_access_all_departments', label: 'Доступ ко всем подразделениям' },
    ] },
    { id: 'mobile', label: 'Мобильное приложение', permissions: [
        { key: 'can_access_mobile', label: 'Доступ в мобильное приложение' },
        { key: 'can_access_schedule_in_mobile', label: 'Показывать раздел «График»' },
        { key: 'can_access_shift_exchange_in_mobile', label: 'Показывать раздел «Биржа смен»' },
        { key: 'can_access_messages_in_mobile', label: 'Показывать раздел «Сообщения»' },
    ] },
    { id: 'schedule', label: 'График и табель', permissions: [
        { key: 'can_view_schedule_and_timesheet', label: 'Просмотр графика и табеля' },
        { key: 'can_create_schedule', label: 'Создание графика' },
        { key: 'can_edit_day_type', label: 'Редактирование типа дня' },
        { key: 'can_edit_day_state_duration', label: 'Продолжительность для КО' },
        { key: 'can_permanently_edit_day_state_duration', label: 'Безусловное редактирование продолжительности КО' },
        { key: 'can_approve_schedule', label: 'Утверждение графика' },
        { key: 'can_upload_schedule', label: 'Загрузка графика' },
        { key: 'can_download_schedule', label: 'Выгрузка графика' },
        { key: 'can_edit_shift_schedule', label: 'Редактирование графика смен' },
        { key: 'can_edit_timesheet', label: 'Редактирование табеля' },
        { key: 'extended_cell_info', label: 'Расширенная информация ячейки' },
    ] },
    { id: 'fact', label: 'Фактическое время и контроль', permissions: [
        { key: 'can_create_check_points', label: 'Создание контрольных точек' },
        { key: 'can_edit_fact_time', label: 'Редактирование фактического времени' },
        { key: 'can_edit_past_fact_time', label: 'Редактирование фактического времени в прошлом' },
    ] },
    { id: 'history', label: 'История', permissions: [
        { key: 'can_view_shift_history', label: 'Просмотр истории смен' },
    ] },
    { id: 'reports', label: 'Отчёты и выгрузки', permissions: [
        { key: 'can_download_timesheet', label: 'Выгрузка табеля' },
        { key: 'can_download_fraud_report', label: 'Выгрузка отчета Фрод' },
        { key: 'can_download_manual_marks_report', label: 'Скачать отчёт об отметках' },
        { key: 'can_download_outsource_report', label: 'Выгрузка отчета Аутсорс' },
        { key: 'can_download_work_time_report', label: 'Отчет по рабочему времени' },
        { key: 'can_download_discipline_report', label: 'Выгрузка отчета Дисциплина' },
    ] },
    { id: 'employees', label: 'Сотрудники', permissions: [
        { key: 'can_view_user_list', label: 'Просмотр сотрудников' },
        { key: 'can_create_user', label: 'Создание сотрудника' },
        { key: 'can_edit_user_main_info', label: 'Редактирование основной информации сотрудника' },
        { key: 'can_change_smart_checkin_possible', label: 'Изменение настроек СмартОтметки' },
        { key: 'can_reset_user_password', label: 'Восстановление пароля сотрудника' },
        { key: 'can_view_user_archive', label: 'Просмотр архива сотрудников' },
        { key: 'can_delete_user', label: 'Удаление сотрудника' },
    ] },
    { id: 'employment', label: 'Трудоустройство', permissions: [
        { key: 'can_create_employment', label: 'Создание трудоустройства' },
        { key: 'can_edit_employment', label: 'Редактирование трудоустройства' },
        { key: 'can_delete_employment', label: 'Удаление трудоустройства' },
    ] },
    { id: 'vacancies', label: 'Вакансии', permissions: [
        { key: 'can_view_shift_exchange', label: 'Просмотр биржи смен' },
        { key: 'can_create_vacancy_exchange', label: 'Создание обмена вакансиями / сменами' },
        { key: 'can_view_vacancies', label: 'Просмотр вакансий' },
        { key: 'can_view_vacancy_requests', label: 'Просмотр запросов на вакансии' },
        { key: 'can_create_vacancy_request', label: 'Создание запроса на вакансию' },
        { key: 'can_confirm_vacancy_request', label: 'Подтверждение запроса на вакансию' },
        { key: 'can_confirm_user_for_vacancy', label: 'Подтверждение сотрудника на вакансию' },
    ] },
    { id: 'system', label: 'Справочники и настройки системы', permissions: [
        { key: 'can_manage_shift_exchange', label: 'Управление биржей смен' },
        { key: 'can_edit_account_settings', label: 'Редактирование настроек аккаунта' },
        { key: 'can_create_department', label: 'Создание подразделений' },
        { key: 'can_edit_position', label: 'Редактирование должностей' },
        { key: 'can_edit_roles', label: 'Редактирование ролей' },
        { key: 'can_manage_analytics', label: 'Управление аналитикой' },
    ] },
]

export const ROLE_PERMISSIONS: ReadonlyArray<RolePermission & { groupId: string, groupLabel: string }> =
    ROLE_GROUPS.flatMap((group) => group.permissions.map((permission) => ({ ...permission, groupId: group.id, groupLabel: group.label })))

const permissionLabel = (key: string) => ROLE_PERMISSIONS.find((permission) => permission.key === key)?.label ?? key

/**
 * Зависимости прав.
 * soft — выключение родителя выключает детей, дети остаются доступны (прежнее поведение).
 * hard — дочернее право недоступно при выключенном родителе (прежний disabled).
 */
export const ROLE_DEPENDENCIES: ReadonlyArray<{ parent: RoleBooleanKey, children: RoleBooleanKey[], hard: boolean }> = [
    {
        parent: 'can_view_schedule_and_timesheet',
        children: ['can_create_schedule', 'can_edit_day_type', 'can_approve_schedule', 'can_upload_schedule', 'can_download_schedule', 'can_edit_shift_schedule', 'can_edit_timesheet'],
        hard: false,
    },
    { parent: 'can_edit_day_state_duration', children: ['can_permanently_edit_day_state_duration'], hard: true },
]

export type RoleValues = Partial<Record<RoleBooleanKey, boolean>> & { [ROLE_DAYS_KEY]?: number | null }
/** Черновик роли: булевы правки и введённый текст числового параметра. */
export type RoleDraft = Partial<Record<RoleBooleanKey, boolean>> & { [ROLE_DAYS_KEY]?: string }

/** Прежняя нормализация отображения роли (значения по умолчанию для отсутствующих ключей). */
export function normalizeRole(role: Role): RoleValues {
    const values: RoleValues = {}
    ROLE_PERMISSIONS.forEach(({ key }) => { values[key] = Boolean(role[key]) })
    values.can_access_schedule_in_mobile = role.can_access_schedule_in_mobile ?? true
    values.can_access_shift_exchange_in_mobile = role.can_access_shift_exchange_in_mobile ?? true
    values.can_access_messages_in_mobile = role.can_access_messages_in_mobile ?? true
    values.can_permanently_edit_day_state_duration = role.can_edit_day_state_duration
        ? Boolean(role.can_permanently_edit_day_state_duration)
        : false
    values[ROLE_DAYS_KEY] = role[ROLE_DAYS_KEY] ?? null
    return values
}

export function parseRoleDays(text: string | undefined): { value: number | null, valid: boolean } {
    const trimmed = String(text ?? '').trim()
    if (!trimmed) return { value: null, valid: true }
    const value = parseNonNegativeInt(trimmed)
    return { value, valid: value !== null }
}

export function effectiveRoleValue(baseline: RoleValues, draft: RoleDraft, key: RoleBooleanKey): boolean {
    return key in draft ? Boolean(draft[key]) : Boolean(baseline[key])
}

/** Применить переключение права с зависимостями. Возвращает новый черновик (правки, равные baseline, убираются). */
export function setRolePermissions(baseline: RoleValues, draft: RoleDraft, updates: Partial<Record<RoleBooleanKey, boolean>>): RoleDraft {
    const next: RoleDraft = { ...draft, ...updates }
    ROLE_DEPENDENCIES.forEach((dependency) => {
        const parentUpdated = dependency.parent in updates
        if (!parentUpdated || effectiveRoleValue(baseline, next, dependency.parent)) return
        dependency.children.forEach((child) => { next[child] = false })
    })
    return pruneRoleDraft(baseline, next)
}

export function pruneRoleDraft(baseline: RoleValues, draft: RoleDraft): RoleDraft {
    const next: RoleDraft = { ...draft }
    ROLE_PERMISSIONS.forEach(({ key }) => {
        if (key in next && Boolean(next[key]) === Boolean(baseline[key])) delete next[key]
    })
    if (ROLE_DAYS_KEY in next) {
        const parsed = parseRoleDays(next[ROLE_DAYS_KEY])
        if (parsed.valid && parsed.value === (baseline[ROLE_DAYS_KEY] ?? null)) delete next[ROLE_DAYS_KEY]
    }
    return next
}

export function isRoleDaysInvalid(draft: RoleDraft): boolean {
    return ROLE_DAYS_KEY in draft && !parseRoleDays(draft[ROLE_DAYS_KEY]).valid
}

export function rolePatch(baseline: RoleValues, draft: RoleDraft): RoleValues {
    const patch: RoleValues = {}
    ROLE_PERMISSIONS.forEach(({ key }) => {
        if (key in draft && Boolean(draft[key]) !== Boolean(baseline[key])) patch[key] = Boolean(draft[key])
    })
    if (ROLE_DAYS_KEY in draft) {
        const parsed = parseRoleDays(draft[ROLE_DAYS_KEY])
        if (parsed.valid && parsed.value !== (baseline[ROLE_DAYS_KEY] ?? null)) patch[ROLE_DAYS_KEY] = parsed.value
    }
    return patch
}

/** Причина, по которой отмена правки права даст недопустимый набор (hard-зависимость). */
export function roleRevertBlockedReason(baseline: RoleValues, draft: RoleDraft, key: RoleBooleanKey): string | undefined {
    const dependency = ROLE_DEPENDENCIES.find((item) => item.hard && item.children.includes(key))
    if (!dependency) return undefined
    if (baseline[key] && !effectiveRoleValue(baseline, draft, dependency.parent)) {
        return `Доступно только при включённом праве «${permissionLabel(dependency.parent)}»`
    }
    return undefined
}

export function roleChanges(baseline: RoleValues, draft: RoleDraft): SettingsChange[] {
    const changes: SettingsChange[] = []
    ROLE_PERMISSIONS.forEach(({ key, label, groupLabel }) => {
        if (!(key in draft) || Boolean(draft[key]) === Boolean(baseline[key])) return
        changes.push({
            id: `role:${key}`,
            block: groupLabel,
            label,
            techKey: key,
            before: boolText(baseline[key]),
            after: boolText(draft[key]),
            revertBlockedReason: roleRevertBlockedReason(baseline, draft, key),
            anchor: `permission-${key}`,
        })
    })
    if (ROLE_DAYS_KEY in draft) {
        const parsed = parseRoleDays(draft[ROLE_DAYS_KEY])
        changes.push({
            id: `role:${ROLE_DAYS_KEY}`,
            block: 'Фактическое время и контроль',
            label: 'Дней в прошлом для редактирования факта',
            techKey: ROLE_DAYS_KEY,
            before: baseline[ROLE_DAYS_KEY] === null || baseline[ROLE_DAYS_KEY] === undefined ? 'глобальное значение' : String(baseline[ROLE_DAYS_KEY]),
            after: !parsed.valid ? 'некорректно' : parsed.value === null ? 'глобальное значение' : String(parsed.value),
            invalid: !parsed.valid,
            anchor: `permission-${ROLE_DAYS_KEY}`,
        })
    }
    return changes
}

export function revertRoleChange(baseline: RoleValues, draft: RoleDraft, changeId: string): RoleDraft {
    const key = changeId.replace(/^role:/, '') as RoleBooleanKey | typeof ROLE_DAYS_KEY
    if (key !== ROLE_DAYS_KEY && roleRevertBlockedReason(baseline, draft, key)) return draft
    const next: RoleDraft = { ...draft }
    delete next[key]
    // Отмена правки родителя проходит те же зависимости: если родитель вернулся к «выкл.»,
    // зависимые права выключаются; возврат родителя к «вкл.» детей не включает.
    ROLE_DEPENDENCIES.forEach((dependency) => {
        if (dependency.parent !== key || effectiveRoleValue(baseline, next, dependency.parent)) return
        dependency.children.forEach((child) => { next[child] = false })
    })
    return pruneRoleDraft(baseline, next)
}

/* ---------- Должности ---------- */

export interface PositionValues {
    short_name: string | null
    shift_schedule: string | null
}

export type PositionDraft = Partial<PositionValues>

export const POSITION_SHORT_NAME_MAX = 3

export const isShortNameChanged = (current: string | null | undefined, original: string | null | undefined) =>
    (current ?? '') !== (original ?? '')

export function positionPatch(baseline: PositionValues, draft: PositionDraft): PositionDraft {
    const patch: PositionDraft = {}
    if ('short_name' in draft && isShortNameChanged(draft.short_name, baseline.short_name)) patch.short_name = draft.short_name ?? null
    if ('shift_schedule' in draft && draft.shift_schedule !== baseline.shift_schedule) patch.shift_schedule = draft.shift_schedule ?? null
    return patch
}

export function prunePositionDraft(baseline: PositionValues, draft: PositionDraft): PositionDraft {
    return positionPatch(baseline, draft)
}

/* ---------- Текстовые подписи значений ---------- */

export function globalChange(
    baseline: GlobalSettings,
    draft: GlobalDraft,
    key: GlobalKey,
    block: string,
    label: string,
    unit = '',
): SettingsChange | null {
    const invalid = isGlobalDraftInvalid(draft, key)
    if (!isGlobalDraftChanged(baseline, draft, key) && !invalid) return null
    const value = draft[key]
    const isBool = typeof value === 'boolean'
    return {
        id: `global:${key}`,
        block,
        label,
        before: isBool ? boolText(baseline[key] as boolean) : valueText(baseline[key] as number, unit),
        after: isBool ? boolText(value) : invalid ? 'некорректно' : valueText(parseNonNegativeInt(value as string), unit),
        invalid,
        anchor: `setting-${key}`,
    }
}
